import { useMemo, useState } from "react";
import { useComponentDidMount } from "../../../lib/hook";
import get from "lodash/get";
import identity from "lodash/identity";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import omit from "lodash/omit";
import groupBy from "lodash/groupBy";
import { Col, FormControl, Grid, Row, Glyphicon } from "react-bootstrap";
import { If, classNames } from "react-extras";
import Table from "../../lib/tables";
import { connect } from "react-redux";
import {
  EpisodesByShowActionManager,
  ShowsForUserActionManager,
} from "../../../action_managers/shows";
import { getMarkersByShowUUID } from "../../../action_managers/markers";
import React from "react";
import { AlbumArt } from "../../lib/album-art";
import {
  TriStateCheckmark,
  CheckMark,
  checkedState,
  notCheckedState,
  defaultCheckedState,
} from "../../lib/utility_components";
import getFp from "lodash/fp/get";
const getCheckMarkState = (markers, toCheck, markerUUID, audioBlockUUID) => {
  if (toCheck[markerUUID]) {
    return checkedState;
  }
  if (toCheck[markerUUID] === false) {
    return notCheckedState;
  }
  if (!!audioBlockUUID && markers[markerUUID].audioBlockUUID === audioBlockUUID) {
    return checkedState;
  }
  return markers[markerUUID].audioBlockUUID ? defaultCheckedState : notCheckedState;
};

const categoryToFriendlyNames = { PREROLL: "Pre Roll", MIDROLL: "Mid Roll", POSTROLL: "Post Roll" };

const EpisodeCell = props => {
  return (
    <div className={"episode-cell"}>
      <AlbumArt
        src={props.original.imageURL}
        alt="album-art"
        className={"episode-cell-album-art"}
      />
      <span className={"margin-left--small bold"}>{props.value.title}</span>
    </div>
  );
};

const getColumnCheckedState = (
  markers,
  toCheck,
  audioBlockUUID,
  columnPosition,
  showUUID,
  markersByShowUUID
) => {
  const stateSet = get(markersByShowUUID, showUUID, [])
    .map(markerUUID => markers[markerUUID])
    .filter(marker => marker.position === columnPosition)
    .reduce((agg, marker) => {
      agg.add(getCheckMarkState(markers, toCheck, marker.uuid, audioBlockUUID));
      return agg;
    }, new Set());
  if (stateSet.size > 1) {
    return defaultCheckedState;
  }
  if (stateSet.size === 1) {
    return Array.from(stateSet.values())[0];
  }
};

const columnInfo = (
  onClick,
  markers,
  toCheck,
  audioBlockUUID,
  filters,
  showUUID,
  onColumnClick,
  markersByShowUUID
) => [
  {
    accessor: "episode",
    Header: "episode",
    width: 300,
    resizable: false,
    Cell: props => <EpisodeCell {...props} />,
  },
  ...["PREROLL", "MIDROLL", "POSTROLL"].map(columnPosition => ({
    accessor: "markers",
    resizable: false,
    Header: () => {
      const checkMarkState = getColumnCheckedState(
        markers,
        toCheck,
        audioBlockUUID,
        columnPosition,
        showUUID,
        markersByShowUUID
      );
      return (
        <div className={"flex-row-container align-center"}>
          <TriStateCheckmark
            state={checkMarkState}
            onClick={() => onColumnClick(checkMarkState, columnPosition)}
          />
          <span className={"margin-left--small margin-bottom--none"}>{columnPosition}</span>
        </div>
      );
    },
    Cell: props => {
      if (!filters[columnPosition]) {
        return null;
      }
      return (
        <div className={"checkmarks-container"}>
          {get(props, "value", [])
            .filter(({ position }) => position === columnPosition)
            .map(marker => (
              <TriStateCheckmark
                metadata={marker.uuid}
                key={marker.uuid}
                state={getCheckMarkState(markers, toCheck, marker.uuid, audioBlockUUID)}
                onClick={() => onClick(marker.uuid)}
              />
            ))}
        </div>
      );
    },
  })),
];

export let RunBlockSelector = props => {
  const [searchString, setSearchString] = useState("");
  const [showUUID, setShowUUID] = useState("");
  const [showFilter, updateShowFilter] = useState(false);
  const [filters, updateFilters] = useState({
    PREROLL: true,
    MIDROLL: true,
    POSTROLL: true,
  });

  const markerMapByUUID = useMemo(() => {
    return Object.keys(props.markers.markers).reduce((agg, markerUUID) => {
      const marker = props.markers.markers[markerUUID];
      if (props.audioBlockUUID === marker.audioBlockUUID) {
        agg.add(markerUUID);
      }
      return agg;
    }, new Set());
  }, [props.markers.markers, props.audioBlockUUID]);

  const { toCheck, updateCheck } = props;

  const handleChange = e => {
    const showUUID = e.target.value;
    setShowUUID(showUUID);
    if (isEmpty(props.episodesByShow[showUUID])) {
      props.getEpisodesForShow(showUUID);
    }
    if (isEmpty(get(props.markers, ["markerUUIDsByShow", showUUID]))) {
      props.getMarkersByShowUUID(showUUID);
    }
  };

  useComponentDidMount(() => {
    if (isEmpty(props.shows)) {
      props.getShowForUser().then(resp => {
        if (resp.status === 200 && resp.json.length === 1) {
          handleChange({ target: { value: resp.json[0].uuid } });
        }
      });
    } else {
      if (Object.keys(props.shows).length === 1) {
        handleChange({ target: { value: Object.keys(props.shows)[0].uuid } });
      }
    }
  }, []);

  const onClick = markerUUID => {
    const state = getCheckMarkState(
      props.markers.markers,
      toCheck,
      markerUUID,
      props.audioBlockUUID
    );
    let newState;
    switch (state) {
      case checkedState:
        newState = markerMapByUUID.has(markerUUID)
          ? { ...toCheck, [markerUUID]: false }
          : omit(toCheck, markerUUID);
        break;
      case defaultCheckedState:
        newState = { ...toCheck, [markerUUID]: true };
        break;
      case notCheckedState:
        if (
          props.audioBlockUUID &&
          props.markers.markers[markerUUID].audioBlockUUID === props.audioBlockUUID
        ) {
          newState = omit(toCheck, markerUUID);
        } else {
          newState = { ...toCheck, [markerUUID]: true };
        }
        break;
      default:
        newState = state;
    }

    updateCheck(newState);
  };

  const markersByEpisodes = useMemo(
    () => getMarkersByEpisode(props.markers.markersByShowUUID, props.markers.markers),
    [props.markers.markersByShowUUID, props.markers.markers]
  );

  const show = getShow(props, showUUID, {});

  let episodes = useMemo(() => {
    return map(get(props.episodesByShow, [showUUID, "episodes"], {}), identity)
      .filter(ep => ep.title.toLowerCase().includes(searchString.toLowerCase()))
      .map(episode => ({
        episode,
        markers: markersByEpisodes[episode.uuid],
        imageURL: get(episode, "imageURL") || get(show, "imageURL"),
      }));
  }, [markersByEpisodes, props.episodesByShow, searchString, showUUID, show]);

  const addAll = columnPosition => {
    toCheckForEach(columnPosition, (agg, marker) => {
      agg[marker.uuid] = true;
      return agg;
    });
  };

  const removeAll = columnPosition => {
    toCheckForEach(columnPosition, (agg, marker) => {
      if (props.markers.markers[marker.uuid].audioBlockUUID === props.audioBlockUUID) {
        agg[marker.uuid] = false;
      } else {
        delete agg[marker.uuid];
      }
      return agg;
    });
  };

  const toCheckForEach = (columnPosition, reducer) => {
    const toADD = episodes.filter(getFp("markers")).reduce(
      (agg, { markers }) => {
        return markers.filter(marker => marker.position === columnPosition).reduce(reducer, agg);
      },
      { ...toCheck }
    );
    updateCheck(toADD);
  };

  const onColumnClick = (checkMarkState, columnPosition) => {
    if (checkMarkState === checkedState) {
      removeAll(columnPosition);
    } else {
      addAll(columnPosition);
    }
  };

  const numberChanged = groupBy(toCheck);

  const options = [
    <option key={"default-option"} disabled value={""}>
      Select Show
    </option>,
  ].concat(
    Object.keys(props.shows)
      .filter(showUUID => props.shows[showUUID].isVisible !== false)
      .map(showUUID => (
        <option key={showUUID} value={showUUID}>
          {getShow(props, showUUID).title}
        </option>
      ))
  );

  return (
    <Grid fluid>
      <Row>
        <Col xs={12} md={8} mdOffset={2} className={"position-relative"}>
          <p>Select the insertion points where you'd like this audio block to play.</p>
          <div
            className={classNames(
              "selection-indicator",
              (!!get(numberChanged, "true.length") || !!get(numberChanged, "false.length")) &&
                "show"
            )}>
            <If condition={!!get(numberChanged, "true.length")}>
              {get(numberChanged, "true.length")} Insertion Points Added
            </If>
            <If
              condition={
                !!get(numberChanged, "true.length") && !!get(numberChanged, "false.length")
              }>
              ,
            </If>
            <If condition={!!get(numberChanged, "false.length")}>
              {get(numberChanged, "false.length")} Insertion Points Removed
            </If>
          </div>
          <div className={"flex-row-container margin-bottom--small"}>
            <div className="flex-1" />
            <span className="fake-link margin-left--small" onClick={() => updateCheck({})}>
              Reset
            </span>
          </div>
          <Row>
            <Col xs={12} md={11}>
              <FormControl
                componentClass={"select"}
                id={"select-show"}
                value={showUUID}
                onChange={handleChange}>
                {options}
              </FormControl>
            </Col>
            <Col xs={12} md={1}>
              <div
                className={classNames("filter-button", showFilter && "active")}
                onClick={() => updateShowFilter(!showFilter)}>
                <Glyphicon glyph={"th-list"} />
              </div>
            </Col>
          </Row>
          <If condition={showFilter}>
            <Row className={"filter-row"}>
              <Col xs={12} md={4}>
                <FormControl
                  type={"text"}
                  className={"filter-episodes-input"}
                  placeHolder={`Filter Episodes...`}
                  id={"filter-episodes"}
                  value={searchString}
                  onChange={e => setSearchString(e.target.value)}
                />
              </Col>
              <Col xs={12} md={8}>
                {["PREROLL", "MIDROLL", "POSTROLL"].map(categoryName => (
                  <Col xs={4} key={categoryName}>
                    <span className={"flex-row-container align-center"}>
                      <CheckMark
                        checked={filters[categoryName]}
                        onClick={() => {
                          updateFilters({ ...filters, [categoryName]: !filters[categoryName] });
                        }}
                      />
                      <span className={"filter-label"}>
                        {categoryToFriendlyNames[categoryName]}
                      </span>
                    </span>
                  </Col>
                ))}
              </Col>
            </Row>
          </If>
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={8} mdOffset={2}>
          <If condition={!!episodes.length}>
            <Table
              getTdProps={() => ({ style: { paddingLeft: 0 } })}
              sortable={false}
              data={episodes}
              columns={columnInfo(
                onClick,
                props.markers.markers,
                toCheck,
                props.audioBlockUUID,
                filters,
                get(show, "uuid"),
                onColumnClick,
                props.markers.markersByShowUUID
              )}
              className="audio-block-episode-table"
              showPagination={false}
            />
          </If>
        </Col>
      </Row>
    </Grid>
  );
};

const getMarkersByEpisode = (markerUUIDsByShow, markers) => {
  return Object.keys(markerUUIDsByShow).reduce((agg, showUUID) => {
    markerUUIDsByShow[showUUID]
      .map(markerUUID => markers[markerUUID])
      .forEach(marker => {
        agg[marker.episodeUUID] = agg[marker.episodeUUID] || [];
        agg[marker.episodeUUID].push(marker);
      });
    return agg;
  }, {});
};

const getShow = (props, uuid) => props.shows[uuid];

RunBlockSelector = connect(
  state => ({
    shows: state.shows.shows,
    episodesByShow: state.episodesByShow,
    markers: state.markers,
  }),
  {
    getShowForUser: () => new ShowsForUserActionManager().run(),
    getEpisodesForShow: showUUID => new EpisodesByShowActionManager({ showUUID }).run(),
    getMarkersByShowUUID,
  }
)(RunBlockSelector);
